import React from 'react';
import { Eye, Database, Globe, AlertCircle, Lock, Shield, UserCheck } from 'lucide-react';

const PrivacyContentRussian = () => {
  return (
    <>
      {/* Section 1 */}
      <section id="general" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
          1. Общие положения
        </h2>
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Настоящая Политика описывает, как Polyus Plus OÜ (далее — Компания) обрабатывает 
            персональные данные в соответствии с Регламентом (ЕС) 2016/679 (GDPR) и Законом 
            Эстонии о защите персональных данных.
          </p>
          <p>
            Компания выступает в качестве:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>Контроллера данных</strong> (при работе сайта и маркетинге)</li>
            <li><strong>Обработчика данных</strong> (при оказании услуг клиентам по договорам и DPA)</li>
          </ul>
        </div>
      </section>

      {/* Section 2 */}
      <section id="data" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
          2. Какие данные мы обрабатываем
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <Eye className="w-6 h-6 text-blue-400" />
              <h3 className="text-xl font-semibold text-white">Персональные данные</h3>
            </div>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-1">•</span>
                <span>Имя, должность</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-1">•</span>
                <span>Контактные данные (e-mail, телефон)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-1">•</span>
                <span>Данные форм и переписки</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <Database className="w-6 h-6 text-blue-400" />
              <h3 className="text-xl font-semibold text-white">Данные компании (B2B)</h3>
            </div>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-1">•</span>
                <span>Название компании</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-1">•</span>
                <span>Регистрационный номер</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-1">•</span>
                <span>Юридический адрес</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <Globe className="w-6 h-6 text-blue-400" />
              <h3 className="text-xl font-semibold text-white">Техническая информация</h3>
            </div>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-1">•</span>
                <span>IP-адрес</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-1">•</span>
                <span>Cookies и идентификаторы</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-1">•</span>
                <span>Данные аналитики</span>
              </li>
            </ul>
          </div>

          <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="w-6 h-6 text-red-400" />
              <h3 className="text-xl font-semibold text-red-400">Важно</h3>
            </div>
            <p className="text-gray-300">
              Мы НЕ собираем чувствительные персональные данные (расовое происхождение, религия, здоровье)
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 */}
      <section id="purposes" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
          3. Цели и правовые основания
        </h2>
        <p className="text-gray-300 mb-6">Обработка осуществляется для:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Исполнение договора</h3>
            <p className="text-sm text-gray-400 mb-2">Art. 6(1)(b) GDPR</p>
            <p className="text-gray-300">Коммуникация, выставление счетов, оказание услуг</p>
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Маркетинг</h3>
            <p className="text-sm text-gray-400 mb-2">Art. 6(1)(a) GDPR</p>
            <p className="text-gray-300">С вашего согласия (можно отозвать в любой момент)</p>
          </div>

          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Аналитика и улучшение сайта</h3>
            <p className="text-sm text-gray-400 mb-2">Art. 6(1)(f) GDPR</p>
            <p className="text-gray-300">Законный интерес компании</p>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Юридические обязательства</h3>
            <p className="text-sm text-gray-400 mb-2">Art. 6(1)(c) GDPR</p>
            <p className="text-gray-300">Налоговое и бухгалтерское законодательство</p>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section id="cookies" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
          4. Cookies и аналитика
        </h2>
        <p className="text-gray-300 mb-4">
          Сайт использует cookies и сервисы аналитики для улучшения пользовательского опыта 
          и понимания, как посетители используют наш сайт.
        </p>
        <div className="space-y-3">
          <div className="flex items-start gap-3 bg-gray-800/30 rounded-lg p-4">
            <span className="text-blue-400">•</span>
            <div>
              <strong className="text-white">Google Analytics 4:</strong>
              <span className="text-gray-400"> Анализ трафика и поведения</span>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-gray-800/30 rounded-lg p-4">
            <span className="text-blue-400">•</span>
            <div>
              <strong className="text-white">Meta Pixel (Facebook):</strong>
              <span className="text-gray-400"> Реклама и ремаркетинг</span>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-gray-800/30 rounded-lg p-4">
            <span className="text-blue-400">•</span>
            <div>
              <strong className="text-white">Hotjar:</strong>
              <span className="text-gray-400"> Тепловые карты и UX-анализ</span>
            </div>
          </div>
        </div>
        <p className="text-gray-400 mt-4">
          Вы можете управлять cookies через настройки браузера или наши настройки. 
        </p>
        <a href="/cookie-policy" className="text-blue-400 hover:underline">
          Подробнее в Политике cookies →
        </a>
      </section>

      {/* Section 5 */}
      <section id="ai-services" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
          5. AI и облачные сервисы
        </h2>
        <p className="text-gray-300 mb-4">
          Для выполнения услуг Polyus Plus OÜ использует современные AI-платформы и облачные сервисы:
        </p>
        <ul className="list-disc list-inside text-gray-400 space-y-2">
          <li>OpenAI (ChatGPT, DALL-E)</li>
          <li>Google Cloud AI</li>
          <li>Microsoft Azure</li>
          <li>Notion, Airtable</li>
        </ul>
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mt-4">
          <p className="text-gray-300">
            При передаче данных за пределы ЕС применяется защита в виде <strong>Стандартных договорных 
            клаузул ЕС (SCC)</strong> и иных механизмов, одобренных Европейской комиссией.
          </p>
        </div>
      </section>

      {/* Section 6 */}
      <section id="transfer" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
          6. Передача и хранение данных
        </h2>
        <p className="text-gray-300 mb-4">Данные могут передаваться только:</p>
        <div className="space-y-4">
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-semibold mb-2">Сотрудникам и подрядчикам Polyus Plus OÜ</h4>
            <p className="text-gray-400">С подписанными соглашениями о конфиденциальности</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-semibold mb-2">Провайдерам хостинга, облака и аналитики</h4>
            <p className="text-gray-400">AWS, Google Cloud, Vercel и др. (с DPA)</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-semibold mb-2">Государственным органам</h4>
            <p className="text-gray-400">Только по законным запросам (суд, налоговая служба)</p>
          </div>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mt-6">
          <h4 className="text-white font-semibold mb-2">Срок хранения</h4>
          <p className="text-gray-300">
            <strong className="text-blue-400">До 5 лет</strong> после окончания договора, 
            если иное не требуется законом (например, налоговое или бухгалтерское 
            законодательство — 7 лет).
          </p>
        </div>
      </section>

      {/* Section 7 */}
      <section id="rights" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
          7. Права субъектов данных
        </h2>
        <p className="text-gray-300 mb-4">В соответствии с GDPR у вас есть следующие права:</p>
        <div className="space-y-3">
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-semibold mb-1">Право на доступ</h4>
            <p className="text-gray-400">Получить копию ваших персональных данных</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-semibold mb-1">Право на исправление</h4>
            <p className="text-gray-400">Исправить некорректные данные</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-semibold mb-1">Право на удаление</h4>
            <p className="text-gray-400">"Право быть забытым" (при определённых условиях)</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-semibold mb-1">Право на ограничение</h4>
            <p className="text-gray-400">Ограничить обработку ваших данных</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-semibold mb-1">Право на переносимость</h4>
            <p className="text-gray-400">Получить данные в структурированном формате</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-semibold mb-1">Право на возражение</h4>
            <p className="text-gray-400">Отозвать согласие на обработку</p>
          </div>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mt-6">
          <h4 className="text-lg font-semibold text-green-400 mb-3">Как реализовать свои права?</h4>
          <p className="text-gray-300 mb-3">
            Направьте запрос на info@polyusplus.com. Мы ответим в течение 30 дней.
          </p>
          <p className="text-gray-400 text-sm">
            Вы также имеете право подать жалобу в надзорный орган: <strong>Andmekaitse Inspektsioon</strong> 
            (Служба защиты данных Эстонии) — aki.ee
          </p>
        </div>
      </section>

      {/* Section 8 */}
      <section id="security" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
          8. Безопасность
        </h2>
        <p className="text-gray-300 mb-6">
          Компания применяет современные технические и организационные меры для защиты ваших 
          персональных данных от несанкционированного доступа, утечки, изменения или уничтожения.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center">
            <Lock className="w-10 h-10 text-blue-400 mx-auto mb-3" />
            <h4 className="text-white font-semibold mb-2">Шифрование</h4>
            <p className="text-gray-400 text-sm">SSL/TLS для передачи данных</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center">
            <Shield className="w-10 h-10 text-blue-400 mx-auto mb-3" />
            <h4 className="text-white font-semibold mb-2">Контроль доступа</h4>
            <p className="text-gray-400 text-sm">Многофакторная аутентификация</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center">
            <Database className="w-10 h-10 text-blue-400 mx-auto mb-3" />
            <h4 className="text-white font-semibold mb-2">Защищённые серверы</h4>
            <p className="text-gray-400 text-sm">AWS, Google Cloud с DPA</p>
          </div>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <h4 className="text-white font-semibold mb-2">Уведомление о нарушении</h4>
          <p className="text-gray-300">
            В случае нарушения защиты персональных данных мы уведомим надзорный орган в течение 
            72 часов и вас лично, если нарушение несёт высокий риск для ваших прав и свобод.
          </p>
        </div>
      </section>

      {/* Section 9 - Contacts */}
      <section id="contacts" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
          9. Контакты
        </h2>
        <p className="text-gray-300 mb-4">
          По вопросам обработки персональных данных или для реализации ваших прав свяжитесь с нами:
        </p>
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <p className="text-white font-semibold mb-2">Polyus Plus OÜ</p>
          <p className="text-gray-400">Рег. № 14890902</p>
          <p className="text-gray-400">Uuslinna 11, Tallinn 11415, Estonia</p>
          <p className="text-gray-400 mt-3">
            <a href="mailto:info@polyusplus.com" className="text-blue-400 hover:underline">
              info@polyusplus.com
            </a>
          </p>
        </div>
      </section>
    </>
  );
};

export default PrivacyContentRussian;
